import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../stores/authStore';

interface MFAForm {
  mfaToken: string;
}

interface MFADialogProps {
  isOpen: boolean;
  onClose: () => void;
  username: string;
  password: string;
  mfaChallenge: any;
  rememberMe?: boolean;
}

export const MFADialog: React.FC<MFADialogProps> = ({
  isOpen,
  onClose,
  username,
  password,
  mfaChallenge,
  rememberMe = false,
}) => {
  const { login, resendMFACode, isLoading } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<MFAForm>();

  const isEmailMFA = mfaChallenge?.type === 'email';

  const onSubmit = async (data: MFAForm) => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      
      // Auto-lowercase username if it looks like an email
      const normalizedUsername = username.includes('@') ? username.toLowerCase() : username;
      
      const result = await login({
        username: normalizedUsername,
        password,
        mfaToken: data.mfaToken,
        rememberMe,
      });

      // If login successful, it won't require MFA anymore
      if (!result.requiresMFA) {
        onClose();
        reset();
      }
    } catch (error: any) {
      // Error handling is done in the auth store
      console.error('MFA verification failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (!isEmailMFA) return;

    try {
      // Auto-lowercase username if it looks like an email
      const normalizedUsername = username.includes('@') ? username.toLowerCase() : username;
      await resendMFACode(normalizedUsername);
    } catch (error) {
      // Error handling is done in the auth store
      console.error('Failed to resend MFA code:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {isEmailMFA ? 'Email Verification' : 'Two-Factor Authentication'}
          </h3>
          
          <p className="text-sm text-gray-600">
            {isEmailMFA 
              ? mfaChallenge?.message || 'Enter the verification code sent to your email.'
              : 'Enter the verification code from your authenticator app.'
            }
          </p>

          {isEmailMFA && mfaChallenge?.expiryMinutes && (
            <p className="text-xs text-orange-600 mt-2">
              Code expires in {mfaChallenge.expiryMinutes} minutes
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label htmlFor="mfaToken" className="block text-sm font-medium text-gray-700 mb-1">
              Verification Code
            </label>
            <div className="relative">
              <input
                id="mfaToken"
                type={showToken ? 'text' : 'password'}
                autoComplete="one-time-code"
                className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder={isEmailMFA ? "Enter 6-digit code" : "Enter code from app"}
                {...register('mfaToken', {
                  required: 'Verification code is required',
                  pattern: {
                    value: isEmailMFA ? /^\d{6}$/ : /^[0-9]{6,8}$/,
                    message: isEmailMFA ? 'Code must be 6 digits' : 'Invalid code format'
                  }
                })}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowToken(!showToken)}
              >
                {showToken ? (
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L2.633 2.633m7.245 7.245l8.487 8.487M12 15.75a3.75 3.75 0 01-3.75-3.75" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {errors.mfaToken && (
              <p className="mt-1 text-sm text-red-600">{errors.mfaToken.message}</p>
            )}
          </div>

          <div className="flex flex-col space-y-2">
            <button
              type="submit"
              disabled={isSubmitting || isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting || isLoading ? 'Verifying...' : 'Verify & Login'}
            </button>

            {isEmailMFA && (
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isLoading}
                className="w-full text-sm text-blue-600 hover:text-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Didn't receive the code? Send again
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className="w-full text-sm text-gray-600 hover:text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};